<script lang="ts" setup>
import { useStore } from '@nanostores/vue'
import { computed, ref, watch } from 'vue'
import {
  $isPrintSimulationLoaded,
  $isPrintSimulationLoading,
  $previewDataUri,
  $printSimulationDataUri,
  $printSimulationError
} from './product-customization-board.store'
import IcSharpCode from '#components/icons/IcSharpCode.vue'

import 'floating-vue/dist/style.css'

const sliderPosition = defineModel({
  type: Number,
  default: 100
})

const previewDataUri = useStore($previewDataUri)
const printSimulationDataUri = useStore($printSimulationDataUri)
const printSimulationError = useStore($printSimulationError)
const isPrintSimulationLoading = useStore($isPrintSimulationLoading)
const isPrintSimulationLoaded = useStore($isPrintSimulationLoaded)

const root = ref<HTMLElement | null>(null)
const slider = ref<HTMLElement | null>(null)
const isDragging = ref(false)

const isComparisonActive = computed(
  () => !isPrintSimulationLoading.value && isPrintSimulationLoaded.value && !printSimulationError.value
)

watch(isComparisonActive, (isActive) => {
  if (!isActive) {
    sliderPosition.value = 100
  } else {
    sliderPosition.value = 50
  }
})

const startDrag = (event: MouseEvent | TouchEvent) => {
  if (!root.value || !slider.value || !isComparisonActive.value) return

  isDragging.value = true

  const moveSlider = (_event: MouseEvent | TouchEvent) => {
    const rootRect = root.value?.getBoundingClientRect()
    const sliderRect = slider.value?.getBoundingClientRect()

    if (!rootRect || !sliderRect) return

    const rootLeft = rootRect.left
    const rootRight = rootRect.right
    const x = _event instanceof MouseEvent ? _event.clientX : _event.touches[0]?.clientX || 0

    sliderPosition.value = Math.max(0, Math.min(100, ((x - rootLeft) / (rootRight - rootLeft)) * 100))
  }

  const onDrag = (_event: MouseEvent | TouchEvent) => {
    if (!isDragging.value) return

    moveSlider(_event)
  }

  const endDrag = () => {
    isDragging.value = false

    window.removeEventListener('mousemove', onDrag)
    window.removeEventListener('touchmove', onDrag)
    window.removeEventListener('mouseup', endDrag)
    window.removeEventListener('touchend', endDrag)
  }

  moveSlider(event)

  window.addEventListener('mousemove', onDrag)
  window.addEventListener('touchmove', onDrag)
  window.addEventListener('mouseup', endDrag)
  window.addEventListener('touchend', endDrag)
}
</script>

<template>
  <Transition>
    <div
      v-show="previewDataUri"
      ref="root"
      class="tw-relative tw-flex tw-w-full tw-select-none tw-flex-col tw-justify-center tw-gap-2"
    >
      <span
        class="product-customization-image-comparison__label tw-justify-start"
        :class="{ 'tw-opacity-20': isComparisonActive && sliderPosition === 0 }"
      >
        Current Preview
      </span>

      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
      <div
        class="product-customization-image-comparison__main"
        :class="isComparisonActive ? (isDragging ? 'tw-cursor-grabbing' : 'tw-cursor-grab') : ''"
        @mousedown="startDrag"
        @touchstart="startDrag"
      >
        <img
          :src="previewDataUri"
          alt=""
          class="tw-pointer-events-none tw-h-auto tw-w-full tw-object-cover tw-transition-all tw-duration-200 tw-ease-out"
          :style="{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }"
        />
        <img
          :src="printSimulationDataUri || previewDataUri"
          alt=""
          class="tw-pointer-events-none tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-transition-all tw-duration-200 tw-ease-out"
          :style="{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }"
        />

        <div
          ref="slider"
          class="product-customization-image-comparison__slider"
          :class="{ 'tw-opacity-0': !isComparisonActive }"
          :style="{ left: `${sliderPosition}%` }"
        >
          <span
            class="tw-absolute tw-left-1/2 tw-top-1/2 tw-flex tw-h-8 tw-w-8 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-items-center tw-justify-center tw-rounded-[50%] tw-bg-white tw-shadow-md md:tw-h-10 md:tw-w-10"
          >
            <IcSharpCode class="tw-h-5 tw-w-5 md:tw-h-7 md:tw-w-7" />
          </span>
        </div>
      </div>

      <span
        class="product-customization-image-comparison__label tw-justify-end"
        :class="{ 'tw-opacity-20': isComparisonActive && sliderPosition === 100, 'tw-opacity-0': !isComparisonActive }"
      >
        Print Simulation
      </span>
    </div>
  </Transition>
</template>

<style lang="postcss" scoped>
.product-customization-image-comparison__main {
  @apply tw-relative tw-flex tw-h-fit tw-w-full tw-overflow-hidden tw-rounded-lg;

  box-shadow: 0px 4px 5px 1px rgba(0, 0, 0, 0.5);
}

.product-customization-image-comparison__label {
  @apply tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-[rgb(var(--color-secondary-button-text))] tw-transition-opacity lg:tw-text-sm;
}

.product-customization-image-comparison__slider {
  @apply tw-absolute tw-bottom-0 tw-top-0 tw-z-[1] !tw-block tw-w-[2px] tw-bg-white tw-transition-[left] tw-duration-200 tw-ease-out;

  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.3);
}
</style>
